import { API_BASE } from "../config.js";

/**
 * Cliente SSE de /api/chat/:id/events con dedupe por id de mensaje, replay por
 * Last-Event-ID y fallback a polling del historial si el stream falla repetido.
 *
 * A diferencia de la reconexión nativa de EventSource, acá se controla el ciclo
 * de vida a mano para:
 *  - cerrar el stream en background (visibilitychange→hidden / pagehide) y
 *    reabrirlo en `visible`, así el server deja de ver presencia y sí manda el
 *    Web Push (H10/B4). El replay usa el último id visto (?lastEventId=).
 *  - reintentar con backoff exponencial + jitter con tope (en vez del fijo de
 *    5 s), pausado mientras la pestaña está `hidden` u `offline`.
 *  - en un cierre HTTP (401 token vencido, etc.) pedir a la app que renueve la
 *    sesión de forma transparente (onAuthError) en vez de quedar muda para
 *    siempre.
 */

const POLL_INTERVAL_MS = 10_000;
const FAILURES_BEFORE_POLLING = 3;
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_CAP_MS = 30_000;

let source = null;
let conversationId = null;
let sessionToken = null;
let callbacks = {};
let failures = 0;
let reconnectAttempts = 0;
let pollTimer = null;
let reconnectTimer = null;
let lastEventId = null;
// Se pide UNA renovación de sesión por stream que efectivamente abrió: sólo
// source.onopen lo vuelve a false. Antes se reseteaba en openEventStream, que
// es justo lo que la propia renovación llama al reabrir el stream — con un
// stream que falla siempre (proxy que rompe el SSE, conversación borrada) eso
// era un loop: cada vuelta creaba una conversación nueva y repetía el saludo.
let authErrorNotified = false;

const seenIds = new Set();

export function markSeen(id) {
  if (id) seenIds.add(String(id));
}

export function wasSeen(id) {
  return id ? seenIds.has(String(id)) : false;
}

/**
 * Backoff exponencial con tope y jitter (helper puro y testeable).
 * @param {number} attempt intento (0-based)
 */
export function computeBackoff(attempt, { base = RECONNECT_BASE_MS, cap = RECONNECT_CAP_MS, random = Math.random } = {}) {
  const exp = Math.min(cap, base * 2 ** Math.max(0, attempt));
  const jitter = exp * 0.3 * random();
  return Math.round(exp + jitter);
}

function isHidden() {
  return typeof document !== "undefined" && document.visibilityState === "hidden";
}

function isOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

export function openEventStream(convId, token, cbs) {
  closeEventStream();
  conversationId = convId;
  sessionToken = token;
  callbacks = cbs || {};
  // Semilla del replay: el id del último mensaje que la app YA tiene (del
  // historial hidratado). Sin ella, un stream que todavía no recibió ningún
  // evento reconecta sin lastEventId y el server no repone nada — todo lo
  // publicado con la pestaña oculta (el SSE se suspende a propósito para que
  // dispare el push) se perdía hasta un F5. También cubre la carrera entre el
  // fetch de /history y la primera conexión.
  lastEventId = cbs?.initialLastEventId ? String(cbs.initialLastEventId) : null;
  seenIds.clear();
  (cbs?.initialSeenIds || []).forEach(markSeen);
  connect();
}

export function closeEventStream() {
  if (source) { source.close(); source = null; }
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  conversationId = null;
  sessionToken = null;
  failures = 0;
  reconnectAttempts = 0;
}

// Suspende el stream sin perder la config (conversationId/token): se reabre en
// `visible`/`online`. No cuenta como fallo → no dispara polling ni backoff.
function suspend() {
  if (source) { source.close(); source = null; }
  if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
}

function resume() {
  if (!conversationId) return;
  reconnectAttempts = 0;
  if (!source && !reconnectTimer) connect();
}

function scheduleReconnect() {
  if (reconnectTimer || !conversationId) return;
  // Pausado en background/offline: se reanuda en el evento correspondiente.
  if (isHidden() || isOffline()) return;
  const delay = computeBackoff(reconnectAttempts);
  reconnectAttempts += 1;
  reconnectTimer = setTimeout(() => { reconnectTimer = null; connect(); }, delay);
}

function connect() {
  if (!conversationId || typeof EventSource === "undefined") return;
  if (isHidden() || isOffline()) return;

  let url = `${API_BASE}/api/chat/${conversationId}/events?token=${encodeURIComponent(sessionToken)}`;
  if (lastEventId) url += `&lastEventId=${encodeURIComponent(lastEventId)}`;
  source = new EventSource(url);

  for (const type of ["agent_message", "bot_message", "csat_request", "handoff"]) {
    source.addEventListener(type, (event) => {
      let payload;
      try { payload = JSON.parse(event.data); } catch { return; }
      const id = payload?.id || event.lastEventId || null;
      if (id) {
        lastEventId = String(id);
        if (wasSeen(id)) return;
        markSeen(id);
      }
      callbacks.onEvent?.(type, payload);
    });
  }

  source.onopen = () => {
    failures = 0;
    reconnectAttempts = 0;
    authErrorNotified = false;
    if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  };

  source.onerror = () => {
    failures += 1;
    if (source?.readyState === EventSource.CLOSED) {
      // Cierre HTTP definitivo (4xx). El caso típico es 401 por token vencido:
      // pedir una renovación transparente de sesión. Se pide una sola vez por
      // stream abierto (ver authErrorNotified): si la renovación funciona, el
      // onopen del stream nuevo rehabilita el pedido para el próximo
      // vencimiento; si el stream está roto de raíz, no se insiste y sólo
      // sigue el backoff.
      source = null;
      if (!authErrorNotified && callbacks.onAuthError) {
        authErrorNotified = true;
        Promise.resolve(callbacks.onAuthError()).catch(() => {});
      }
      scheduleReconnect();
    }
    if (failures >= FAILURES_BEFORE_POLLING && !pollTimer) {
      pollTimer = setInterval(() => callbacks.onFallbackPoll?.(), POLL_INTERVAL_MS);
    }
  };
}

// ── Ciclo de vida de la pestaña ─────────────────────────────────────────────
// Se registra una sola vez al importar el módulo. Sin efectos en entornos sin
// document/window (tests en node).
if (typeof document !== "undefined" && typeof document.addEventListener === "function") {
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") suspend();
    else resume();
  });
}
if (typeof window !== "undefined" && typeof window.addEventListener === "function") {
  window.addEventListener("pagehide", () => suspend());
  window.addEventListener("online", () => resume());
  window.addEventListener("offline", () => suspend());
}
